import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  TextField,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  useForkRef,
  useTheme,
} from "@mui/material";
import TooltipDialogCst from "@components/TooltipDialogCst";
import { useFetchOptions } from "@hooks/useFetchOptions";
import { slugify } from "@utils/utilsTestIds";
import { MultiSelectInterface } from "./types";

const MIN_INPUT_WIDTH = 24;
const TAGS_GAP_FALLBACK = 6;

const getChipLabel = (option: any) =>
  option && typeof option === "object"
    ? (option.label ?? option.value)
    : option;

const SCROLL_END_THRESHOLD = 24;

const ScrollEndContext = React.createContext<{
  loading?: boolean;
  onScrollEnd?: () => void;
}>({});

const ScrollEndListbox = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLElement>
>(function ScrollEndListbox({ children, onScroll, ...listboxProps }, ref) {
  const { loading, onScrollEnd } = React.useContext(ScrollEndContext);
  const listboxRef = useRef<HTMLUListElement>(null);
  const handleRef = useForkRef(listboxRef, ref);

  useEffect(() => {
    const listbox = listboxRef.current;
    if (listbox && listbox.scrollHeight <= listbox.clientHeight) {
      onScrollEnd?.();
    }
  });

  return (
    <ul
      {...listboxProps}
      ref={handleRef}
      onScroll={(event) => {
        onScroll?.(event);
        const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
        if (scrollTop + clientHeight >= scrollHeight - SCROLL_END_THRESHOLD) {
          onScrollEnd?.();
        }
      }}
    >
      {children}
      {loading && (
        <Box
          component="li"
          sx={{ display: "flex", justifyContent: "center", py: "6px" }}
        >
          <CircularProgress size={14} />
        </Box>
      )}
    </ul>
  );
});

const MultiSelectCst: React.FC<MultiSelectInterface> = (props) => {
  const {
    id,
    label,
    value,
    onChange,
    options,
    disabled,
    error = false,
    errorMessage = "",
    customWidth,
    textTooltip,
    optionsKey,
    optionsToDisable,
    service,
    required,
    sx,
    isFilterActive,
    testIdPrefix,
    limitTags = 1,
    fitTagsInOneLine = false,
    onScrollEnd,
    loading,
  } = props;

  const autocompleteId = testIdPrefix ? `${testIdPrefix}-input` : id;
  const inputTestId = testIdPrefix ? `${testIdPrefix}-input` : undefined;
  const theme = useTheme();
  const { t: tForm } = useTranslation(["form"]);

  const [data, setData] = React.useState<any[]>([]);

  const getFormattedValue = () => {
    let formattedValue: any[] = [];
    value &&
      value?.forEach((val: any) => {
        if (typeof val === "string") {
          formattedValue.push(
            data.find((opt) => opt.value?.toLowerCase() === val?.toLowerCase()),
          );
        } else if (val.value) {
          formattedValue.push(
            data.find(
              (opt) => opt.value?.toLowerCase() === val?.value?.toLowerCase(),
            ),
          );
        }
      });
    return formattedValue;
  };

  useEffect(() => {
    // if initialValue is array of string trasform the value of field in array of object based on options
    if (
      data &&
      data.length > 0 &&
      value &&
      value?.some((elem: any) => elem && !elem.value)
    ) {
      const values = getFormattedValue();
      onChange([...(values || [])]);
    }
  }, [value, data]);

  const { getOptions } = useFetchOptions(service, optionsKey);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    if (options?.length > 0) {
      setData(options);
    } else if (getOptions()) {
      setData(getOptions());
    }
  }, [options, getOptions]);

  const containerRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const [visibleTags, setVisibleTags] = useState<number | null>(null);

  const selectedTags: any[] = Array.isArray(value) ? value : [];
  const tagsKey = selectedTags.map(getChipLabel).join("|");

  useLayoutEffect(() => {
    if (!fitTagsInOneLine) return;
    const root = containerRef.current?.querySelector<HTMLElement>(
      ".MuiAutocomplete-inputRoot",
    );
    const ghost = ghostRef.current;
    if (!root || !ghost) return;

    const measure = () => {
      const nodes = Array.from(ghost.children) as HTMLElement[];
      if (nodes.length < 2) return;
      const overflowWidth = nodes[nodes.length - 1].offsetWidth;
      const widths = nodes.slice(0, -1).map((node) => node.offsetWidth);
      const styles = window.getComputedStyle(root);
      const gap = parseFloat(styles.columnGap) || TAGS_GAP_FALLBACK;
      const endAdornment = root.querySelector<HTMLElement>(
        ".MuiAutocomplete-endAdornment",
      );
      const available =
        root.clientWidth -
        (parseFloat(styles.paddingLeft) || 0) -
        (parseFloat(styles.paddingRight) || 0) -
        (endAdornment?.offsetWidth || 0) -
        MIN_INPUT_WIDTH;

      let used = 0;
      let count = 0;
      widths.forEach((width) => {
        const next = used + width + (count > 0 ? gap : 0);
        if (next <= available) {
          used = next;
          count += 1;
        }
      });

      // se qualcosa resta fuori serve spazio anche per il chip "+N"
      if (count < widths.length) {
        while (count > 0 && used + gap + overflowWidth > available) {
          count -= 1;
          used -= widths[count] + (count > 0 ? gap : 0);
        }
      }

      setVisibleTags((prev) => (prev === count ? prev : count));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    // il clearIndicator compare col focus e cambia lo spazio utile: va osservato anche lui
    const endAdornmentNode = root.querySelector<HTMLElement>(
      ".MuiAutocomplete-endAdornment",
    );
    if (endAdornmentNode) observer.observe(endAdornmentNode);
    return () => observer.disconnect();
  }, [fitTagsInOneLine, tagsKey, data]);

  return (
    <Box
      ref={containerRef}
      sx={{
        minWidth: "200px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: customWidth ? customWidth : "auto",
        ...sx,
      }}
    >
      {textTooltip && (
        <TooltipDialogCst inputDialog title={label} textTooltip={textTooltip} />
      )}
      <ScrollEndContext.Provider value={{ loading, onScrollEnd }}>
        <Autocomplete
          multiple
          id={autocompleteId}
          options={data}
          loading={loading}
          loadingText={tForm("loading-state")}
          {...(onScrollEnd ? { ListboxComponent: ScrollEndListbox } : {})}
          getOptionDisabled={(option) => {
            return optionsToDisable?.includes(option.value) || false;
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isFilterActive ? theme.palette.text.primary : "",
            },
            ...(fitTagsInOneLine && {
              "& .MuiAutocomplete-inputRoot": {
                flexWrap: "nowrap",
                overflow: "hidden",
              },
              "& .MuiAutocomplete-input": { minWidth: `${MIN_INPUT_WIDTH}px` },
            }),
          }}
          limitTags={fitTagsInOneLine ? -1 : limitTags}
          disabled={disabled}
          disableCloseOnSelect
          onChange={(event: any, newValue: any) => {
            onChange([...newValue]);
          }}
          isOptionEqualToValue={(option: any, selected: any) => {
            const lowerValueValues =
              typeof selected === "string"
                ? selected
                : typeof selected?.value === "string"
                  ? selected?.value
                  : "";
            let arrayOfPossibleValueOk = [
              selected,
              selected?.value,
              lowerValueValues,
            ];
            return arrayOfPossibleValueOk.includes(option.value);
          }}
          value={value || []}
          renderOption={(props, option, { selected }) => {
            return (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.label}
              </li>
            );
          }}
          size="small"
          renderTags={(tagValue, getTagProps) => {
            const visible =
              fitTagsInOneLine && visibleTags !== null
                ? tagValue.slice(0, visibleTags)
                : tagValue;
            const hidden = tagValue.slice(visible.length);
            const chips = visible.map((option: any, index: number) => {
              const chipLabel = getChipLabel(option);
              const chipTestId = testIdPrefix
                ? `${testIdPrefix}-chip-${slugify(chipLabel)}`
                : undefined;
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  label={chipLabel}
                  size="small"
                  variant="outlined"
                  {...(chipTestId ? { "data-testid": chipTestId } : {})}
                  deleteIcon={
                    <CancelIcon
                      {...(chipTestId
                        ? {
                            "data-testid": `${chipTestId}-remove`,
                            "aria-label": tForm(
                              "testAutomation.chipRemoveAria",
                            ),
                          }
                        : {})}
                    />
                  }
                />
              );
            });

            if (hidden.length === 0) return <>{chips}</>;

            return (
              <>
                {chips}
                <Tooltip title={hidden.map(getChipLabel).join(", ")}>
                  <Chip
                    label={`+${hidden.length}`}
                    size="small"
                    variant="outlined"
                    {...(testIdPrefix
                      ? { "data-testid": `${testIdPrefix}-chip-overflow` }
                      : {})}
                  />
                </Tooltip>
              </>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              error={error}
              required={required}
              helperText={error ? errorMessage : ""}
              inputProps={{
                ...params.inputProps,
                ...(inputTestId ? { "data-testid": inputTestId } : {}),
              }}
            />
          )}
        />
      </ScrollEndContext.Provider>
      {fitTagsInOneLine && (
        <Box
          ref={ghostRef}
          aria-hidden
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            height: 0,
            display: "flex",
            gap: `${TAGS_GAP_FALLBACK}px`,
            overflow: "hidden",
            visibility: "hidden",
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          {selectedTags.map((option: any, index: number) => (
            <Chip
              key={index}
              label={getChipLabel(option)}
              size="small"
              variant="outlined"
              onDelete={() => undefined}
              deleteIcon={<CancelIcon />}
            />
          ))}
          <Chip
            label={`+${selectedTags.length}`}
            size="small"
            variant="outlined"
          />
        </Box>
      )}
    </Box>
  );
};

export default MultiSelectCst;
